import type { SummaryEntry } from "components/selection-components/summary/summary-row";
import { MenuStatus, type BookingMenu } from "types/menu";
import type { MenuGroup } from "types/menu-group";
import type { WebBooking } from "types/web-booking";

interface StandaloneConfirmedMenuSummary {
    groups: MenuGroup[];
    price: number;
}

function getSelectedMenuGroups(menu: BookingMenu): MenuGroup[] {
    return (
        menu.groups
            ?.map((group) => ({
                ...group,
                items: group.items?.filter((item) => item.quantity > 0) ?? [],
            }))
            .filter((group) => group.items.length > 0) ?? []
    );
}

function getMenuTotalPrice(groups: MenuGroup[]): number {
    return groups.reduce(
        (menuPrice, group) =>
            menuPrice +
            (group.items?.reduce(
                (groupPrice, item) => groupPrice + item.price * item.quantity,
                0
            ) ?? 0),
        0
    );
}

function getStandaloneConfirmedMenus(reservation: WebBooking): BookingMenu[] {
    const menuGuidsConnectedToActivities = new Set(
        reservation.articles?.map((article) => article.menuGuid).filter(Boolean) ?? []
    );

    return reservation.menus.filter(
        (menu) =>
            menu.status === MenuStatus.Confirmed &&
            !menuGuidsConnectedToActivities.has(menu.publicId)
    );
}

export function getStandaloneConfirmedMenuSummary(
    reservation: WebBooking
): StandaloneConfirmedMenuSummary | null {
    const groups = getStandaloneConfirmedMenus(reservation).flatMap((menu) =>
        getSelectedMenuGroups(menu)
    );

    if (groups.length === 0) {
        return null;
    }

    return {
        groups,
        price: getMenuTotalPrice(groups),
    };
}

export function getStandaloneConfirmedMenuEntries(reservation: WebBooking): SummaryEntry[] {
    return getStandaloneConfirmedMenus(reservation).flatMap((menu) => {
        const groups = getSelectedMenuGroups(menu);
        if (groups.length === 0) {
            return [];
        }

        return [
            {
                key: `menu-${menu.publicId}`,
                name: { sv: menu.name },
                imageUrl: "",
                startTime: reservation.start,
                endTime: reservation.start,
                price: getMenuTotalPrice(groups),
                priceText: null,
                groups,
                children: [],
                isExperience: false,
                trackAttribute: "summary-menu",
            },
        ];
    });
}
